<template>
    <div class="modal fade" id="completionModal" tabindex="-1" aria-labelledby="completionModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg">
            <div class="modal-content">

                <div class="modal-body">
                    <form @submit.prevent="submitCompletion" enctype="multipart/form-data">
                        <div class="row">

                            <!-- Vehicle Make -->
                            <div class="col-md-6 form-group mb-3">
                                <label class="control-label">Vehicle Make</label>
                                <input
                                    class="form-control"
                                    type="text"
                                    v-model="formData.vehicle_make"
                                    placeholder="Enter vehicle make"
                                />
                            </div>

                            <!-- Vehicle Model -->
                            <div class="col-md-6 form-group mb-3">
                                <label class="control-label">Vehicle Model</label>
                                <input
                                    class="form-control"
                                    type="text"
                                    v-model="formData.vehicle_model"
                                    placeholder="Enter vehicle model"
                                />
                            </div>

                            <!-- Registration Date -->
                            <div class="col-md-6 form-group mb-3">
                                <label class="control-label">Registration Date</label>
                                <input
                                    class="form-control"
                                    type="date"
                                    v-model="formData.registration_date"
                                />
                            </div>

                            <!-- Engine Number -->
                            <div class="col-md-6 form-group mb-3">
                                <label class="control-label">Engine Number</label>
                                <input 
                                    class="form-control" 
                                    type="text" 
                                    v-model="formData.engine_no" 
                                    placeholder="Enter engine number" 
                                />
                            </div>

                            <!-- Chassis Number -->
                            <div class="col-md-6 form-group mb-3">
                                <label class="control-label">Chassis Number</label>
                                <input 
                                    class="form-control" 
                                    type="text" 
                                    v-model="formData.chassis_no" 
                                    placeholder="Enter chassis number" 
                                />
                            </div>

                            <!-- RC Image -->
                            <div class="col-md-6 form-group mb-3">
                                <label class="control-label">RC Image</label>
                                <input 
                                    class="form-control" 
                                    type="file" 
                                    @change="handleFileUpload($event, 'rc_image')"
                                    accept="image/*"
                                    ref="rcImageInput"
                                />
                                <div v-if="existingImages.rc_image" class="mt-2">
                                    <small class="text-muted">Current image:</small>
                                    <img :src="imageUrl + existingImages.rc_image" class="img-thumbnail ms-2" width="100" height="100" alt="RC Image">
                                </div>
                                <div v-if="formData.rc_image" class="mt-2">
                                    <small class="text-success">New image selected</small>
                                    <img :src="previewImages.rc_image" class="img-thumbnail ms-2" width="100" height="100" alt="RC Image Preview">
                                </div>
                            </div>

                            <!-- Device Image -->
                            <div class="col-md-6 form-group mb-3">
                                <label class="control-label">Device Image</label>
                                <input 
                                    class="form-control" 
                                    type="file" 
                                    @change="handleFileUpload($event, 'device_image')"
                                    accept="image/*"
                                    ref="deviceImageInput"
                                />
                                <div v-if="existingImages.device_image" class="mt-2">
                                    <small class="text-muted">Current image:</small>
                                    <img :src="imageUrl + existingImages.device_image" class="img-thumbnail ms-2" width="100" height="100" alt="Device Image">
                                </div>
                                <div v-if="formData.device_image" class="mt-2">
                                    <small class="text-success">New image selected</small>
                                    <img :src="previewImages.device_image" class="img-thumbnail ms-2" width="100" height="100" alt="Device Image Preview">
                                </div>
                            </div>

                            <!-- Vehicle Image -->
                            <div class="col-md-6 form-group mb-3">
                                <label class="control-label">Vehicle Image</label>
                                <input 
                                    class="form-control" 
                                    type="file" 
                                    @change="handleFileUpload($event, 'vehicle_image')"
                                    accept="image/*"
                                    ref="vehicleImageInput"
                                />
                                <div v-if="existingImages.vehicle_image" class="mt-2">
                                    <small class="text-muted">Current image:</small>
                                    <img :src="imageUrl + existingImages.vehicle_image" class="img-thumbnail ms-2" width="100" height="100" alt="Vehicle Image">
                                </div>
                                <div v-if="formData.vehicle_image" class="mt-2">
                                    <small class="text-success">New image selected</small>
                                    <img :src="previewImages.vehicle_image" class="img-thumbnail ms-2" width="100" height="100" alt="Vehicle Image Preview">
                                </div>
                            </div>

                            <template v-if="isCameraProduct">
                                <div
                                    class="col-md-12 form-group mb-3"
                                    v-if="showSerialNumberFieldModal && useDealerCameraSerialPickerModal"
                                >
                                    <label class="control-label">Serial Number<span class="text-danger">*</span></label>
                                    <div class="position-relative dealer-camera-dropdown">
                                        <input
                                            class="form-control"
                                            type="text"
                                            placeholder="Search serial number and select a row"
                                            v-model="formData.device_serial_no"
                                            @input="onDealerCameraSerialInput"
                                            @focus="onDealerCameraSerialFocus"
                                            @blur="onDealerCameraSerialBlur"
                                        />
                                        <ul
                                            v-if="showDealerCameraSuggestions && filteredDealerCameras.length"
                                            class="list-group position-absolute w-100"
                                            style="z-index: 9999; max-height: 220px; overflow-y: auto"
                                        >
                                            <li
                                                v-for="row in filteredDealerCameras"
                                                :key="row.id"
                                                class="list-group-item list-group-item-action"
                                                @mousedown.prevent="selectDealerCamera(row)"
                                            >
                                                <span class="fw-semibold">{{ row.serial_number }}</span>
                                                <span class="text-muted ms-2">{{ getDealerCameraNameFromRow(row) }}</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <div class="col-md-12 form-group mb-3" v-else-if="showSerialNumberFieldModal">
                                    <label class="control-label">Serial Number</label>
                                    <input
                                        class="form-control"
                                        type="text"
                                        v-model="formData.device_serial_no"
                                        placeholder="Enter serial number"
                                        :readonly="jobHasExistingCameraSerialDataModal"
                                    />
                                </div>
                                <div class="col-md-12 form-group mb-3" v-if="showSerialNumberFieldModal">
                                    <label class="control-label">No of Cameras<span class="text-danger">*</span></label>
                                    <input
                                        class="form-control"
                                        type="number"
                                        placeholder="1–5"
                                        v-model="formData.no_of_cameras"
                                        min="1"
                                        :max="MAX_VISIBLE_CAMERAS"
                                        step="1"
                                        :readonly="isCameraCountLockedModal"
                                        @input="onNoOfCamerasInput"
                                    />
                                </div>
                                <template v-if="visibleCameraFieldCount > 0">
                                <div class="col-md-6 form-group mb-3" v-if="visibleCameraFieldCount >= 1">
                                    <label class="control-label">Camera Name 1<span class="text-danger">*</span></label>
                                    <input
                                        class="form-control"
                                        type="text"
                                        v-model="formData.camera_name"
                                        placeholder="Enter camera 1 name/serial"
                                        :disabled="useDealerCameraSerialPickerModal || jobHasExistingCameraSerialDataModal"
                                    />
                                </div>
                                <div class="col-md-6 form-group mb-3" v-if="visibleCameraFieldCount >= 2">
                                    <label class="control-label">Camera 2<span class="text-danger">*</span></label>
                                    <input
                                        class="form-control"
                                        type="text"
                                        v-model="formData.camera_2"
                                        placeholder="Enter camera 2 name/serial"
                                        :disabled="useDealerCameraSerialPickerModal || jobHasExistingCameraSerialDataModal"
                                    />
                                </div>

                                <div class="col-md-6 form-group mb-3" v-if="visibleCameraFieldCount >= 3">
                                    <label class="control-label">Camera 3<span class="text-danger">*</span></label>
                                    <input
                                        class="form-control"
                                        type="text"
                                        v-model="formData.camera_3"
                                        placeholder="Enter camera 3 name/serial"
                                        :disabled="useDealerCameraSerialPickerModal || jobHasExistingCameraSerialDataModal"
                                    />
                                </div>

                                <div class="col-md-6 form-group mb-3" v-if="visibleCameraFieldCount >= 4">
                                    <label class="control-label">Camera 4<span class="text-danger">*</span></label>
                                    <input
                                        class="form-control"
                                        type="text"
                                        v-model="formData.camera_4"
                                        placeholder="Enter camera 4 name/serial"
                                        :disabled="useDealerCameraSerialPickerModal || jobHasExistingCameraSerialDataModal"
                                    />
                                </div>

                                <div class="col-md-6 form-group mb-3" v-if="visibleCameraFieldCount >= 5">
                                    <label class="control-label">Camera 5<span class="text-danger">*</span></label>
                                    <input
                                        class="form-control"
                                        type="text"
                                        v-model="formData.camera_5"
                                        placeholder="Enter camera 5 name/serial"
                                        :disabled="useDealerCameraSerialPickerModal || jobHasExistingCameraSerialDataModal"
                                    />
                                </div>
                                </template>
                            </template>
                        </div>
                    </form>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="clearForm">Cancel</button>
                    <button type="button" class="btn btn-primary" @click="submitCompletion" :disabled="isSubmitting">
                        <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {{ isSubmitting ? 'Processing...' : 'Submit' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, inject, onMounted, computed, watch } from 'vue';
import { Modal } from "bootstrap";
import { useRouter } from 'vue-router';
import { get } from '@/utils/api';
import { routes } from '@/constants/routes';
import Swal from '@/utils/swal';
import {
    DEALER_ROLE_ID,
    readStoredLoginDetails,
    parseSettingsDealerProfile,
    resolveAccountIdForAvailableDealerCameras,
    getDealerCameraNameFromRow,
    mapDealerCameraRowToFormFields,
    inferDealerCameraCountFromRow,
    parseVisibleCameraCount,
    resolveNoOfCamerasFromJob,
    trimCamerasOnTarget,
    applyMappedCamerasToTarget,
    validateVisibleCameraFields,
    MAX_VISIBLE_CAMERAS,
} from '@/utils/dealerRole3Profile';

const props = defineProps({
    job: {
        type: Object,
        required: true
    },
    job_id: {
        type: [String, Number],
        required: true
    }
});

const emit = defineEmits(['completed']);

const router = useRouter();
const imageUrl = inject('imageUrl');

const isCameraProduct = computed(() => Number(props.job?.product_id) === 2);

const showSerialNumberFieldModal = computed(
    () => Number(props.job?.product_id) !== 1 && Number(props.job?.service_type) === 1,
);

const user_role_id = ref(null);
const settings_account_id = ref('');
const dealerCamerasList = ref([]);
const showDealerCameraSuggestions = ref(false);
const selectedDealerCameraRow = ref(null);

// Form data (before computeds that read formData)
const formData = ref({
    vehicle_make: '',
    vehicle_model: '',
    registration_date: '',
    engine_no: '',
    chassis_no: '',
    rc_image: null,
    device_image: null,
    vehicle_image: null,
    camera_name: '',
    camera_2: '',
    camera_3: '',
    camera_4: '',
    camera_5: '',
    device_serial_no: '',
    no_of_cameras: '',
});

const isDealerCameraSerialPickerModal = computed(
    () =>
        Number(user_role_id.value) === DEALER_ROLE_ID &&
        isCameraProduct.value &&
        showSerialNumberFieldModal.value,
);

const jobHasExistingCameraSerialDataModal = computed(() => {
    if (!isCameraProduct.value || !showSerialNumberFieldModal.value) return false;
    return String(props.job?.device_serial_no || '').trim() !== '';
});

const useDealerCameraSerialPickerModal = computed(
    () => isDealerCameraSerialPickerModal.value && !jobHasExistingCameraSerialDataModal.value,
);

const visibleCameraFieldCount = computed(() =>
    parseVisibleCameraCount(formData.value.no_of_cameras),
);

const isCameraCountLockedModal = computed(
    () => isCameraProduct.value && jobHasExistingCameraSerialDataModal.value,
);

const filteredDealerCameras = computed(() => {
    const list = dealerCamerasList.value || [];
    const q = (formData.value.device_serial_no || '').trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) => String(r.serial_number || '').toLowerCase().includes(q));
});

// File input refs (needed to clear file selection)
const rcImageInput = ref(null);
const deviceImageInput = ref(null);
const vehicleImageInput = ref(null);

// Existing images from job
const existingImages = ref({});

// Preview images for new uploads
const previewImages = ref({});

// Loading state
const isSubmitting = ref(false);

// Modal instance
let completionModal = null;

const fetchAvailableDealerCameras = () => {
    const did = resolveAccountIdForAvailableDealerCameras(settings_account_id.value);
    if (!did) return;
    get(routes.AvailableDealerCameras, { params: { dealer_id: did } }, false)
        .then((response) => {
            if (response.status === 200) {
                dealerCamerasList.value = response.data.data?.dealer_camera ?? [];
            }
        })
        .catch(() => {});
};

const fetchUserSettings = () =>
    get(routes.Settings, {}, false)
        .then((response) => {
            if (response.status === 200 && response.data?.data) {
                const p = parseSettingsDealerProfile(response.data.data);
                user_role_id.value = p.roleId;
                settings_account_id.value = p.accountId;
            }
        })
        .catch(() => {});

const stripDealerCameraSelection = () => {
    selectedDealerCameraRow.value = null;
    formData.value.camera_name = '';
    formData.value.camera_2 = '';
    formData.value.camera_3 = '';
    formData.value.camera_4 = '';
    formData.value.camera_5 = '';
    formData.value.no_of_cameras = '';
};

const onNoOfCamerasInput = () => {
    const raw = String(formData.value.no_of_cameras ?? '').trim();
    if (raw === '') {
        trimCamerasOnTarget(formData.value, 0);
        return;
    }
    let n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < 1) {
        formData.value.no_of_cameras = '';
        trimCamerasOnTarget(formData.value, 0);
        return;
    }
    if (n > MAX_VISIBLE_CAMERAS) {
        n = MAX_VISIBLE_CAMERAS;
        formData.value.no_of_cameras = String(MAX_VISIBLE_CAMERAS);
    }
    if (selectedDealerCameraRow.value) {
        const count = applyMappedCamerasToTarget(formData.value, selectedDealerCameraRow.value, n);
        formData.value.no_of_cameras = String(count);
    } else {
        trimCamerasOnTarget(formData.value, n);
    }
};

const selectDealerCamera = (row) => {
    selectedDealerCameraRow.value = row;
    formData.value.device_serial_no = row.serial_number || '';
    let count = inferDealerCameraCountFromRow(row);
    const manualCount = parseInt(String(formData.value.no_of_cameras ?? '').trim(), 10);
    if (!count && !Number.isNaN(manualCount) && manualCount >= 1) {
        count = Math.min(MAX_VISIBLE_CAMERAS, manualCount);
    }
    if (!count) count = 1;
    formData.value.no_of_cameras = String(count);
    applyMappedCamerasToTarget(formData.value, row, count);
    showDealerCameraSuggestions.value = false;
};

const tryMatchDealerCameraBySerial = () => {
    const sn = String(formData.value.device_serial_no ?? '').trim();
    if (!sn || !useDealerCameraSerialPickerModal.value) return;
    const row = dealerCamerasList.value.find(
        (r) => String(r.serial_number ?? '').trim() === sn,
    );
    if (row) selectDealerCamera(row);
};

const setCameraFieldsFromJob = () => {
    if (!isCameraProduct.value) {
        formData.value.no_of_cameras = '';
        return;
    }
    formData.value.camera_name = props.job.camera_name || props.job.camera || '';
    formData.value.camera_2 = props.job.camera_2 || '';
    formData.value.camera_3 = props.job.camera_3 || '';
    formData.value.camera_4 = props.job.camera_4 || '';
    formData.value.camera_5 = props.job.camera_5 || '';
    formData.value.device_serial_no = props.job.device_serial_no || '';
    formData.value.no_of_cameras = resolveNoOfCamerasFromJob(props.job);
};

const onDealerCameraSerialInput = () => {
    if (selectedDealerCameraRow.value) {
        const sel = selectedDealerCameraRow.value.serial_number;
        if (String(formData.value.device_serial_no ?? '').trim() !== String(sel ?? '').trim()) {
            stripDealerCameraSelection();
        }
    }
    if (formData.value.device_serial_no && formData.value.device_serial_no.trim() !== '') {
        showDealerCameraSuggestions.value = true;
    } else {
        showDealerCameraSuggestions.value = false;
    }
};

const onDealerCameraSerialFocus = () => {
    if (dealerCamerasList.value.length) {
        showDealerCameraSuggestions.value = true;
    }
};

const onDealerCameraSerialBlur = () => {
    setTimeout(() => {
        showDealerCameraSuggestions.value = false;
        tryMatchDealerCameraBySerial();
    }, 200);
};

watch(useDealerCameraSerialPickerModal, (on) => {
    if (on && resolveAccountIdForAvailableDealerCameras(settings_account_id.value)) {
        fetchAvailableDealerCameras();
    }
    if (!on) {
        selectedDealerCameraRow.value = null;
        showDealerCameraSuggestions.value = false;
    }
});

// File upload handler
const handleFileUpload = (event, fieldName) => {
    const file = event.target.files[0];
    if (file) {
        formData.value[fieldName] = file;
        
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
            previewImages.value[fieldName] = e.target.result;
        };
        reader.readAsDataURL(file);
    }
};

// Initialize form with job data
const initializeForm = () => {
    formData.value.vehicle_make = props.job.vehicle_make || '';
    formData.value.vehicle_model = props.job.vehicle_model || '';
    formData.value.registration_date = props.job.registration_date || '';
    formData.value.engine_no = props.job.engine_no || '';
    formData.value.chassis_no = props.job.chassis_no || '';
    setCameraFieldsFromJob();
    selectedDealerCameraRow.value = null;
    showDealerCameraSuggestions.value = false;
    
    // Set existing images
    existingImages.value = props.job.images || {};
    
    // Clear previous uploads
    formData.value.rc_image = null;
    formData.value.device_image = null;
    formData.value.vehicle_image = null;
    
    previewImages.value = {};
};

// Clear form function - reset to original job data
const clearForm = () => {
    // Reset to original job data (auto-filled fields)
    formData.value.vehicle_make = props.job.vehicle_make || '';
    formData.value.vehicle_model = props.job.vehicle_model || '';
    formData.value.registration_date = props.job.registration_date || '';
    formData.value.engine_no = props.job.engine_no || '';
    formData.value.chassis_no = props.job.chassis_no || '';
    setCameraFieldsFromJob();
    selectedDealerCameraRow.value = null;
    showDealerCameraSuggestions.value = false;
    formData.value.rc_image = null;
    formData.value.device_image = null;
    formData.value.vehicle_image = null;
    
    // Clear preview images (new uploads)
    previewImages.value = {};
    
    // Keep existing images from job data
    existingImages.value = props.job.images || {};
    
    // Clear file inputs
    const fileInputs = [rcImageInput, deviceImageInput, vehicleImageInput];
    fileInputs.forEach((inputRef) => {
        if (inputRef?.value) inputRef.value.value = '';
    });
};

// Show modal
const showModal = () => {
    const ls = readStoredLoginDetails();
    if (ls) {
        const p = parseSettingsDealerProfile(ls);
        if (p.roleId != null) user_role_id.value = p.roleId;
        if (p.accountId) settings_account_id.value = p.accountId;
    }
    initializeForm();
    fetchUserSettings().finally(() => {
        if (useDealerCameraSerialPickerModal.value) {
            fetchAvailableDealerCameras();
        }
    });
    if (completionModal) {
        completionModal.show();
    }
};

// Hide modal
const hideModal = () => {
    if (completionModal) {
        completionModal.hide();
    }
    clearForm();
};

// Submit completion
const submitCompletion = async () => {
    if (isCameraProduct.value && showSerialNumberFieldModal.value) {
        const cameraErr = validateVisibleCameraFields(formData.value, formData.value.no_of_cameras);
        if (cameraErr) {
            Swal.fire('Validation Error', cameraErr, 'error');
            return;
        }
    }

    if (useDealerCameraSerialPickerModal.value) {
        if (!selectedDealerCameraRow.value || !formData.value.device_serial_no.trim()) {
            Swal.fire(
                'Validation Error',
                'Please search and select a serial number from your dealer camera list',
                'error',
            );
            return;
        }
        if (
            String(formData.value.device_serial_no).trim() !==
            String(selectedDealerCameraRow.value.serial_number ?? '').trim()
        ) {
            Swal.fire('Validation Error', 'Serial number must match the selected camera row', 'error');
            return;
        }
    }

    isSubmitting.value = true;

    try {
        // Prepare form data
        const submitData = new FormData();
        submitData.append('job_id', props.job_id);
        
        // Always append fields (even if empty) to avoid undefined array key errors
        submitData.append('vehicle_make', formData.value.vehicle_make || '');
        submitData.append('vehicle_model', formData.value.vehicle_model || '');
        submitData.append('registration_date', formData.value.registration_date || '');
        submitData.append('engine_no', formData.value.engine_no || '');
        submitData.append('chassis_no', formData.value.chassis_no || '');
        if (isCameraProduct.value) {
            submitData.append('camera_name', formData.value.camera_name || '');
            submitData.append('camera_2', formData.value.camera_2 || '');
            submitData.append('camera_3', formData.value.camera_3 || '');
            submitData.append('camera_4', formData.value.camera_4 || '');
            submitData.append('camera_5', formData.value.camera_5 || '');
            submitData.append('no_of_cameras', formData.value.no_of_cameras);
            if (showSerialNumberFieldModal.value && formData.value.device_serial_no) {
                submitData.append('serial_number', formData.value.device_serial_no);
            }
            if (useDealerCameraSerialPickerModal.value && selectedDealerCameraRow.value?.id != null) {
                submitData.append('camera_id', selectedDealerCameraRow.value.id);
            } else if (
                props.job?.camera_id != null &&
                props.job.camera_id !== '' &&
                jobHasExistingCameraSerialDataModal.value
            ) {
                submitData.append('camera_id', props.job.camera_id);
            }
        }
        
        // Only append images if new uploads exist
        if (formData.value.rc_image) {
            submitData.append('rc_image', formData.value.rc_image);
        }
        if (formData.value.device_image) {
            submitData.append('device_image', formData.value.device_image);
        }
        if (formData.value.vehicle_image) {
            submitData.append('vehicle_image', formData.value.vehicle_image);
        }
        // Emit completion event with form data
        emit('completed', submitData);
        
        hideModal();
    } catch (error) {
        console.error('Error submitting completion:', error);
        alert('Error submitting completion. Please try again.');
        clearForm(); // Clear form on error
    } finally {
        isSubmitting.value = false;
    }
};

// Initialize modal on mount
onMounted(() => {
    completionModal = new Modal(document.getElementById('completionModal'));
    const ls = readStoredLoginDetails();
    if (ls) {
        const p = parseSettingsDealerProfile(ls);
        if (p.roleId != null) user_role_id.value = p.roleId;
        if (p.accountId) settings_account_id.value = p.accountId;
    }
    fetchUserSettings().catch(() => {});

    document.addEventListener('click', (e) => {
        if (!e.target.closest('.dealer-camera-dropdown')) {
            showDealerCameraSuggestions.value = false;
        }
    });
});

// Expose methods
defineExpose({
    showModal,
    hideModal,
    clearForm
});
</script>

<style scoped>
.img-thumbnail {
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
}

.text-success {
    color: #198754 !important;
}

.text-muted {
    color: #6c757d !important;
}
</style>
